import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, History as HistoryIcon, CreditCard, TrendingUp, Coins } from "lucide-react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import AdSlot from "@/components/AdSlot";

function StatCard({ label, value, sub, accent, testid }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-6 bg-white border border-[#E2E8F0] rounded-md card-hover"
      data-testid={testid}
    >
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#64748B]">{label}</div>
      <div className={`mt-3 font-display text-4xl font-extrabold ${accent || "text-[#111827]"}`}>{value}</div>
      {sub && <div className="text-sm text-[#64748B] mt-1">{sub}</div>}
    </motion.div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [prompts, setPrompts] = useState([]);

  useEffect(() => {
    api.get("/prompts/history?limit=30").then((r) => setPrompts(r.data.items || []));
  }, []);

  const used = Math.max(0, (user?.tokens_monthly || 0) - (user?.tokens_remaining || 0));
  const monthly = user?.tokens_monthly || 0;
  const remaining = user?.tokens_remaining || 0;

  // Build chart data: last 14 days
  const today = new Date();
  const chartData = Array.from({ length: 14 }).map((_, i) => {
    const d = new Date(today);
    d.setDate(d.getDate() - (13 - i));
    const key = d.toISOString().slice(0, 10);
    const count = prompts.filter((p) => p.created_at?.slice(0, 10) === key).length;
    return { day: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }), prompts: count };
  });

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 sm:mb-10">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#14B8A6] mb-2">Dashboard</div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight">Hello, {user?.name?.split(" ")[0]}.</h1>
          <p className="text-[#64748B] mt-1 text-sm sm:text-base">Your prompt workspace at a glance.</p>
        </div>
        <Link to="/builder" className="shrink-0">
          <Button className="bg-[#14B8A6] hover:bg-[#0D9488] text-white btn-lift gap-2 w-full sm:w-auto" data-testid="dashboard-create-prompt-btn">
            <Sparkles className="w-4 h-4" /> Create prompt
          </Button>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8 sm:mb-10">
        <StatCard
          label="Tokens remaining"
          value={<motion.span key={remaining} initial={{ scale: 0.8 }} animate={{ scale: 1 }}>{remaining}</motion.span>}
          sub={`of ${monthly} monthly`}
          accent="text-[#1F3A8A]"
          testid="stat-tokens-remaining"
        />
        <StatCard label="Tokens used" value={used} sub="this cycle" testid="stat-tokens-used" />
        <StatCard label="Prompts crafted" value={prompts.length} sub="all-time" testid="stat-prompts-crafted" />
        <StatCard label="Current plan" value={(user?.plan || "free").toUpperCase()} sub={user?.subscription_status || "no subscription"} accent="text-[#14B8A6]" testid="stat-current-plan" />
      </div>

      {/* Ad slot — only for free users */}
      <AdSlot variant="banner" className="mb-8 sm:mb-10" />

      {/* Chart + Quick actions */}
      <div className="grid lg:grid-cols-3 gap-6 mb-10">
        <div className="lg:col-span-2 p-6 bg-white border border-[#E2E8F0] rounded-md">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#64748B]">Usage</div>
              <h2 className="font-display text-2xl font-bold">Last 14 days</h2>
            </div>
            <TrendingUp className="w-5 h-5 text-[#14B8A6]" />
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1F3A8A" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#14B8A6" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="day" tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} width={28} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }} />
                <Area type="monotone" dataKey="prompts" stroke="#1F3A8A" strokeWidth={2.5} fill="url(#g1)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="p-6 bg-white border border-[#E2E8F0] rounded-md">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#64748B] mb-4">Quick actions</div>
          <div className="space-y-3">
            <Link to="/builder" className="flex items-center gap-3 p-4 border border-[#E2E8F0] rounded-md hover:border-[#1F3A8A] transition-colors group">
              <div className="w-10 h-10 rounded bg-[#1F3A8A]/10 text-[#1F3A8A] flex items-center justify-center group-hover:bg-[#1F3A8A] group-hover:text-white transition-colors">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <div className="font-semibold text-sm">Create prompt</div>
                <div className="text-xs text-[#64748B]">9 fields · 1 token</div>
              </div>
            </Link>
            <Link to="/history" className="flex items-center gap-3 p-4 border border-[#E2E8F0] rounded-md hover:border-[#1F3A8A] transition-colors group">
              <div className="w-10 h-10 rounded bg-[#1F3A8A]/10 text-[#1F3A8A] flex items-center justify-center group-hover:bg-[#1F3A8A] group-hover:text-white transition-colors">
                <HistoryIcon className="w-5 h-5" />
              </div>
              <div>
                <div className="font-semibold text-sm">View history</div>
                <div className="text-xs text-[#64748B]">{prompts.length} prompts saved</div>
              </div>
            </Link>
            <Link to="/plans" className="flex items-center gap-3 p-4 border border-[#E2E8F0] rounded-md hover:border-[#14B8A6] transition-colors group">
              <div className="w-10 h-10 rounded bg-[#14B8A6]/10 text-[#14B8A6] flex items-center justify-center group-hover:bg-[#14B8A6] group-hover:text-white transition-colors">
                <Coins className="w-5 h-5" />
              </div>
              <div>
                <div className="font-semibold text-sm">Top up tokens</div>
                <div className="text-xs text-[#64748B]">Buy more anytime</div>
              </div>
            </Link>
            <Link to="/plans" className="flex items-center gap-3 p-4 border border-[#E2E8F0] rounded-md hover:border-[#14B8A6] transition-colors group">
              <div className="w-10 h-10 rounded bg-[#14B8A6]/10 text-[#14B8A6] flex items-center justify-center group-hover:bg-[#14B8A6] group-hover:text-white transition-colors">
                <CreditCard className="w-5 h-5" />
              </div>
              <div>
                <div className="font-semibold text-sm">Manage plan</div>
                <div className="text-xs text-[#64748B]">Subscriptions · invoices</div>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Recent prompts */}
      <div className="p-6 bg-white border border-[#E2E8F0] rounded-md">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#64748B]">Recent</div>
            <h2 className="font-display text-2xl font-bold">Latest prompts</h2>
          </div>
          <Link to="/history" className="text-sm text-[#1F3A8A] font-semibold hover:underline">View all →</Link>
        </div>
        {prompts.length === 0 ? (
          <div className="py-16 text-center text-[#64748B]">
            <Sparkles className="w-10 h-10 mx-auto mb-3 text-[#1F3A8A]/30" />
            No prompts yet. <Link to="/builder" className="text-[#14B8A6] font-semibold">Generate your first →</Link>
          </div>
        ) : (
          <div className="space-y-2">
            {prompts.slice(0, 5).map((p) => (
              <Link key={p.id} to="/history" className="flex items-center gap-4 p-3 border border-transparent hover:border-[#E2E8F0] hover:bg-[#F5F7FA] rounded-md transition-colors">
                <div className="font-mono text-xs text-[#64748B] w-24 shrink-0">{new Date(p.created_at).toLocaleDateString()}</div>
                <div className="px-2 py-1 text-xs bg-[#1F3A8A]/10 text-[#1F3A8A] rounded font-semibold">{p.inputs.ai_tool}</div>
                <div className="text-sm text-[#111827] truncate flex-1">{p.inputs.ask}</div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
